import {
	AddressLookupResponse,
	getPlaceDetailsByPlaceId,
	lookUpShippingLocation,
} from '@uturn/api/v1';
import { renderLocation } from '@uturn/portal/modules/shipments/utils';
import { formatSuggestions } from '@uturn/portal/utils/format-address-lookup-suggestions';
import {
	AutoComplete,
	FormControl,
	FormItem,
	FormMessage,
} from '@uturn/ui-kit';
import { useRef, useState } from 'react';
import {
	type ControllerRenderProps,
	type FieldValues,
	useFormContext,
} from 'react-hook-form';
import { locationSignatureFromAddress } from '../multi-update-utils';

type AddressLike = {
	countryCode?: string;
	postalCode?: string;
	city?: string;
	street?: string;
	houseNumber?: string | number;
	latitude?: number;
	longitude?: number;
};

export const LocationInput = ({
	field,
	placeholder,
	emptyMessage,
}: {
	field: ControllerRenderProps<FieldValues, string>;
	placeholder: string;
	emptyMessage: string;
}) => {
	const { setValue: setFormValue, trigger } = useFormContext();
	const [options, setOptions] = useState<string[]>([]);
	const [raw, setRaw] = useState<AddressLookupResponse[]>([]);
	const lastReqId = useRef(0); // avoid race conditions

	const sigField = field.name.replace(/\.value$/, '.locationSignature');

	const writeSelection = (
		value: string | number | undefined,
		addr?: AddressLike,
	) => {
		field.onChange(value);
		const sig = locationSignatureFromAddress(addr);
		setFormValue(sigField, sig, {
			shouldValidate: false,
			shouldDirty: false,
		});
		trigger(field.name);
		field.onBlur();
	};

	const onInputChange = async (val: string) => {
		if (!val) {
			setOptions([]);
			setRaw([]);
			writeSelection(undefined, undefined);
			return;
		}

		const reqId = ++lastReqId.current;
		try {
			const { data } = await lookUpShippingLocation({ searchText: val });
			if (reqId !== lastReqId.current) {
				return;
			}
			setRaw(data);
			setOptions(formatSuggestions(data));
		} catch {
			if (reqId === lastReqId.current) {
				setRaw([]);
				setOptions([]);
			}
		}
	};

	const setSelected = async (index: number) => {
		const loc = raw[index];
		if (!loc) {
			return;
		}

		const { source, id: beId, placeId } = loc;

		if (source === 'GOOGLE') {
			if (!loc.placeId) {
				writeSelection(undefined, undefined);
				return;
			}
			const { data: place } = await getPlaceDetailsByPlaceId({
				placeId: loc.placeId,
			});
			const addr: AddressLike = {
				countryCode: place.country?.countryCode,
				postalCode: place.address?.postalCode,
				city: place.address?.city,
				street: place.address?.street,
				houseNumber: place.address?.houseNumber,
			};

			writeSelection(placeId, addr);
			return;
		}

		// Non-Google (already in our DB)
		else {
			const addr: AddressLike = {
				countryCode: loc.country?.countryCode,
				postalCode: loc.address?.postalCode,
				city: loc.address?.city,
				street: loc.address?.street,
				houseNumber: loc.address?.houseNumber,
			};
			const numericId = Number(beId);
			writeSelection(
				Number.isFinite(numericId) && numericId > 0 ? numericId : undefined,
				addr,
			);
		}
	};

	return (
		<FormItem>
			<FormControl>
				<AutoComplete
					refCallback={field.ref}
					value={renderLocation(field.value) ?? ''}
					options={options.map((s) => ({ label: s, value: s }))}
					emptyMessage={emptyMessage}
					debouncerDelay={1000}
					onInputChange={onInputChange}
					setSelected={setSelected}
					forceUniqueLabelsEnabled
					placeholder={placeholder}
				/>
			</FormControl>
			<FormMessage />
		</FormItem>
	);
};
